import { type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '@/services/api/errors';
import { OrganizationApi } from '@/services/api/organization-api';
import { UserApi } from '@/services/api/user-api';
import type { OrganizationAddRequest } from '@/services/api/types';
import { AuthContext } from '@/features/auth/auth-context';

import { RegistrationContext, type RegistrationContextValue } from './registration-context';

export interface RegistrationProviderProps {
  children: ReactNode;
  /** Test-only injection point for a mock OrganizationApi. */
  organization?: OrganizationApi | undefined;
  /** Test-only injection point for a mock UserApi. */
  user?: UserApi | undefined;
}

/**
 * Owns step 2 (company info) registration state and exposes it via {@link RegistrationContext}.
 *
 * Instantiated per-page inside `OrganizationPage`, not at the app root.
 * The real OrganizationApi is built on the authenticated client from AuthProvider (the
 * organization step lives behind a protected route, so a JWT session is always active).
 */
export function RegistrationProvider({ children, organization, user }: RegistrationProviderProps): ReactNode {
  const auth = useContext(AuthContext);

  const injectedOrganizationApi = useRef(organization);
  useEffect(() => {
    injectedOrganizationApi.current = organization;
  }, [organization]);

  const getOrganizationApi = useCallback((): OrganizationApi => {
    if (injectedOrganizationApi.current) {
      return injectedOrganizationApi.current;
    }
    const client = auth?.client;
    if (!client) {
      throw new ApiError('unknown', 'generic');
    }
    return new OrganizationApi(client);
  }, [auth]);

  const injectedUserApi = useRef(user);
  useEffect(() => {
    injectedUserApi.current = user;
  }, [user]);

  const getUserApi = useCallback((): UserApi => {
    if (injectedUserApi.current) {
      return injectedUserApi.current;
    }
    const client = auth?.client;
    if (!client) {
      throw new ApiError('unknown', 'generic');
    }
    return new UserApi(client);
  }, [auth]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizationSucceeded, setOrganizationSucceeded] = useState(false);

  const submitOrganization = useCallback(
    async (data: OrganizationAddRequest): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        const createdOrganization = await getOrganizationApi().add(data);
        const currentUser = auth?.user;
        const updatedUser = {
          ...(currentUser ?? {}),
          orgID: createdOrganization.id,
          orgName: createdOrganization.nickName ?? currentUser?.orgName ?? null,
          orgDisplayName: createdOrganization.displayName ?? data.displayName,
        };
        try {
          const profile = await getUserApi().getProfile();
          updatedUser.firstname = profile.firstName;
          updatedUser.lastname = profile.lastName;
        } catch (err) {
          if (err instanceof ApiError && err.isUnauthorized) {
            throw err;
          }
        }
        auth?.updateUser(updatedUser);
        auth?.markOrganizationCreated();
        setOrganizationSucceeded(true);
        return true;
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'generic';
        setError(message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [getOrganizationApi, getUserApi, auth],
  );

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<RegistrationContextValue>(
    () => ({ busy, error, organizationSucceeded, submitOrganization, clearError }),
    [busy, error, organizationSucceeded, submitOrganization, clearError],
  );

  return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>;
}
